import _defineProperty from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { computePosition, arrow as arrow$2, offset as offset$1, shift as shift$1, limitShift as limitShift$1, flip as flip$1, size as size$1, autoPlacement as autoPlacement$1, hide as hide$1, inline as inline$1 } from '@floating-ui/dom';
export { autoUpdate, computePosition, detectOverflow, getOverflowAncestors, platform } from '@floating-ui/dom';
import * as React from 'react';
import { useLayoutEffect, useEffect } from 'react';
import * as ReactDOM from 'react-dom';
var index = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

// Fork of `fast-deep-equal` that only does the comparisons we need and compares
// functions
function deepEqual(a, b) {
  if (a === b) {
    return true;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  if (typeof a === 'function' && a.toString() === b.toString()) {
    return true;
  }
  var length;
  var i;
  var keys;
  if (a && b && typeof a === 'object') {
    if (Array.isArray(a)) {
      length = a.length;
      if (length !== b.length) return false;
      for (i = length; i-- !== 0;) {
        if (!deepEqual(a[i], b[i])) {
          return false;
        }
      }
      return true;
    }
    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) {
      return false;
    }
    for (i = length; i-- !== 0;) {
      if (!{}.hasOwnProperty.call(b, keys[i])) {
        return false;
      }
    }
    for (i = length; i-- !== 0;) {
      var key = keys[i];
      if (key === '_owner' && a.$$typeof) {
        continue;
      }
      if (!deepEqual(a[key], b[key])) {
        return false;
      }
    }
    return true;
  }
  return a !== a && b !== b;
}
function getDPR(element) {
  if (typeof window === 'undefined') {
    return 1;
  }
  var win = element.ownerDocument.defaultView || window;
  return win.devicePixelRatio || 1;
}
function roundByDPR(element, value) {
  var dpr = getDPR(element);
  return Math.round(value * dpr) / dpr;
}
function useLatestRef(value) {
  var ref = React.useRef(value);
  index(function () {
    ref.current = value;
  });
  return ref;
}

/**
 * Provides data to position a floating element.
 * @see https://floating-ui.com/docs/useFloating
 */
function useFloating(options) {
  if (options === void 0) {
    options = {};
  }
  var _options = options,
    _options$placement = _options.placement,
    placement = _options$placement === void 0 ? 'bottom' : _options$placement,
    _options$strategy = _options.strategy,
    strategy = _options$strategy === void 0 ? 'absolute' : _options$strategy,
    _options$middleware = _options.middleware,
    middleware = _options$middleware === void 0 ? [] : _options$middleware,
    platform = _options.platform,
    _options$elements = _options.elements,
    _options$elements2 = _options$elements === void 0 ? {} : _options$elements,
    externalReference = _options$elements2.reference,
    externalFloating = _options$elements2.floating,
    _options$transform = _options.transform,
    transform = _options$transform === void 0 ? true : _options$transform,
    whileElementsMounted = _options.whileElementsMounted,
    open = _options.open;
  var _React$useState = React.useState({
      x: 0,
      y: 0,
      strategy: strategy,
      placement: placement,
      middlewareData: {},
      isPositioned: false
    }),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    data = _React$useState2[0],
    setData = _React$useState2[1];
  var _React$useState3 = React.useState(middleware),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    latestMiddleware = _React$useState4[0],
    setLatestMiddleware = _React$useState4[1];
  if (!deepEqual(latestMiddleware, middleware)) {
    setLatestMiddleware(middleware);
  }
  var _React$useState5 = React.useState(null),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    _reference = _React$useState6[0],
    _setReference = _React$useState6[1];
  var _React$useState7 = React.useState(null),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    _floating = _React$useState8[0],
    _setFloating = _React$useState8[1];
  var setReference = React.useCallback(function (node) {
    if (node !== referenceRef.current) {
      referenceRef.current = node;
      _setReference(node);
    }
  }, []);
  var setFloating = React.useCallback(function (node) {
    if (node !== floatingRef.current) {
      floatingRef.current = node;
      _setFloating(node);
    }
  }, []);
  var referenceEl = externalReference || _reference;
  var floatingEl = externalFloating || _floating;
  var referenceRef = React.useRef(null);
  var floatingRef = React.useRef(null);
  var dataRef = React.useRef(data);
  var hasWhileElementsMounted = whileElementsMounted != null;
  var whileElementsMountedRef = useLatestRef(whileElementsMounted);
  var platformRef = useLatestRef(platform);
  var update = React.useCallback(function () {
    if (!referenceRef.current || !floatingRef.current) {
      return;
    }
    var config = {
      placement: placement,
      strategy: strategy,
      middleware: latestMiddleware
    };
    if (platformRef.current) {
      config.platform = platformRef.current;
    }
    computePosition(referenceRef.current, floatingRef.current, config).then(function (data) {
      var fullData = _objectSpread(_objectSpread({}, data), {}, {
        isPositioned: true
      });
      if (isMountedRef.current && !deepEqual(dataRef.current, fullData)) {
        dataRef.current = fullData;
        ReactDOM.flushSync(function () {
          setData(fullData);
        });
      }
    });
  }, [latestMiddleware, placement, strategy, platformRef]);
  index(function () {
    if (open === false && dataRef.current.isPositioned) {
      dataRef.current.isPositioned = false;
      setData(function (data) {
        return _objectSpread(_objectSpread({}, data), {}, {
          isPositioned: false
        });
      });
    }
  }, [open]);
  var isMountedRef = React.useRef(false);
  index(function () {
    isMountedRef.current = true;
    return function () {
      isMountedRef.current = false;
    };
  }, []);
  index(function () {
    if (referenceEl) referenceRef.current = referenceEl;
    if (floatingEl) floatingRef.current = floatingEl;
    if (referenceEl && floatingEl) {
      if (whileElementsMountedRef.current) {
        return whileElementsMountedRef.current(referenceEl, floatingEl, update);
      }
      update();
    }
  }, [referenceEl, floatingEl, update, whileElementsMountedRef, hasWhileElementsMounted]);
  var refs = React.useMemo(function () {
    return {
      reference: referenceRef,
      floating: floatingRef,
      setReference: setReference,
      setFloating: setFloating
    };
  }, [setReference, setFloating]);
  var elements = React.useMemo(function () {
    return {
      reference: referenceEl,
      floating: floatingEl
    };
  }, [referenceEl, floatingEl]);
  var floatingStyles = React.useMemo(function () {
    var initialStyles = {
      position: strategy,
      left: 0,
      top: 0
    };
    if (!elements.floating) {
      return initialStyles;
    }
    var x = roundByDPR(elements.floating, data.x);
    var y = roundByDPR(elements.floating, data.y);
    if (transform) {
      return _objectSpread(_objectSpread({}, initialStyles), {}, {
        transform: "translate(" + x + "px, " + y + "px)"
      }, getDPR(elements.floating) >= 1.5 && {
        willChange: 'transform'
      });
    }
    return {
      position: strategy,
      left: x,
      top: y
    };
  }, [strategy, transform, elements.floating, data.x, data.y]);
  return React.useMemo(function () {
    return _objectSpread(_objectSpread({}, data), {}, {
      update: update,
      refs: refs,
      elements: elements,
      floatingStyles: floatingStyles
    });
  }, [data, update, refs, elements, floatingStyles]);
}

/**
 * Provides data to position an inner element of the floating element so that it
 * appears centered to the reference element.
 * This wraps the core `arrow` middleware to allow React refs as the element.
 * @see https://floating-ui.com/docs/arrow
 */
var arrow$1 = function arrow$1(options) {
  function isRef(value) {
    return {}.hasOwnProperty.call(value, 'current');
  }
  return {
    name: 'arrow',
    options: options,
    fn: function fn(state) {
      var _ref = typeof options === 'function' ? options(state) : options,
        element = _ref.element,
        padding = _ref.padding;
      if (element && isRef(element)) {
        if (element.current != null) {
          return arrow$2({
            element: element.current,
            padding: padding
          }).fn(state);
        }
        return {};
      }
      if (element) {
        return arrow$2({
          element: element,
          padding: padding
        }).fn(state);
      }
      return {};
    }
  };
};

/**
 * Modifies the placement by translating the floating element along the
 * specified axes.
 * A number (shorthand for `mainAxis` or distance), or an axes configuration
 * object may be passed.
 * @see https://floating-ui.com/docs/offset
 */
var offset = function offset(options, deps) {
  return _objectSpread(_objectSpread({}, offset$1(options)), {}, {
    options: [options, deps]
  });
};

/**
 * Optimizes the visibility of the floating element by shifting it in order to
 * keep it in view when it will overflow the clipping boundary.
 * @see https://floating-ui.com/docs/shift
 */
var shift = function shift(options, deps) {
  return _objectSpread(_objectSpread({}, shift$1(options)), {}, {
    options: [options, deps]
  });
};

/**
 * Built-in `limiter` that will stop `shift()` at a certain point.
 */
var limitShift = function limitShift(options, deps) {
  return _objectSpread(_objectSpread({}, limitShift$1(options)), {}, {
    options: [options, deps]
  });
};

/**
 * Optimizes the visibility of the floating element by flipping the `placement`
 * in order to keep it in view when the preferred placement(s) will overflow the
 * clipping boundary. Alternative to `autoPlacement`.
 * @see https://floating-ui.com/docs/flip
 */
var flip = function flip(options, deps) {
  return _objectSpread(_objectSpread({}, flip$1(options)), {}, {
    options: [options, deps]
  });
};

/**
 * Provides data that allows you to change the size of the floating element —
 * for instance, prevent it from overflowing the clipping boundary or match the
 * width of the reference element.
 * @see https://floating-ui.com/docs/size
 */
var size = function size(options, deps) {
  return _objectSpread(_objectSpread({}, size$1(options)), {}, {
    options: [options, deps]
  });
};

/**
 * Optimizes the visibility of the floating element by choosing the placement
 * that has the most space available automatically, without needing to specify a
 * preferred placement. Alternative to `flip`.
 * @see https://floating-ui.com/docs/autoPlacement
 */
var autoPlacement = function autoPlacement(options, deps) {
  return _objectSpread(_objectSpread({}, autoPlacement$1(options)), {}, {
    options: [options, deps]
  });
};

/**
 * Provides data to hide the floating element in applicable situations, such as
 * when it is not in the same clipping context as the reference element.
 * @see https://floating-ui.com/docs/hide
 */
var hide = function hide(options, deps) {
  return _objectSpread(_objectSpread({}, hide$1(options)), {}, {
    options: [options, deps]
  });
};

/**
 * Provides improved positioning for inline reference elements that can span
 * over multiple lines, such as hyperlinks or range selections.
 * @see https://floating-ui.com/docs/inline
 */
var inline = function inline(options, deps) {
  return _objectSpread(_objectSpread({}, inline$1(options)), {}, {
    options: [options, deps]
  });
};

/**
 * Provides data to position an inner element of the floating element so that it
 * appears centered to the reference element.
 * This wraps the core `arrow` middleware to allow React refs as the element.
 * @see https://floating-ui.com/docs/arrow
 */
var arrow = function arrow(options, deps) {
  return _objectSpread(_objectSpread({}, arrow$1(options)), {}, {
    options: [options, deps]
  });
};
export { arrow, autoPlacement, flip, hide, inline, limitShift, offset, shift, size, useFloating };